import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useOrgId } from "@/shared/auth/orgContext";
import {
  stationDirectoryQuery,
  stationUsersQuery,
} from "@/features/users/api/usersQueries";
import type { ReportGenUser } from "@/shared/interfaces/User";
import type { ReportGenReport } from "@/shared/interfaces/ReportGenReport";
import type { EvidenceFileDDB } from "@/shared/interfaces/EvidenceFileDDB";
import {
  listStationReportsSince,
  listStationEvidenceSince,
  aggregateMetrics,
  aggregateOfficerActivity,
  buildActivityFeed,
  filterActivitySince,
  widenToLocalDayStart,
  type ActivityEvent,
  type StationMetrics,
  type OfficerActivity,
} from "../api/stationActivityApi";
import { useLastLoginWindow, type LastLoginWindow } from "./useLastLoginWindow";

export const stationAdminKeys = {
  all: ["stationAdmin"] as const,
  reports: (orgId: string, windowStart: string, windowEnd: string) =>
    [...stationAdminKeys.all, "reports", orgId, windowStart, windowEnd] as const,
  evidence: (orgId: string, windowStart: string, windowEnd: string) =>
    [...stationAdminKeys.all, "evidence", orgId, windowStart, windowEnd] as const,
};

// Stable empties so pre-load renders don't churn downstream memo deps.
const EMPTY_REPORTS: ReportGenReport[] = [];
const EMPTY_EVIDENCE: EvidenceFileDDB[] = [];

function formatOfficer(u: ReportGenUser): string {
  const name = `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim();
  if (name && u.badgeNumber) return `${name} (#${u.badgeNumber})`;
  // id is null for invitation-only rows, which never make it into nameById.
  return name || u.email || u.id || "";
}

export interface UseStationActivityResult {
  window: LastLoginWindow;
  metrics: StationMetrics;
  feed: ActivityEvent[];
  /** Window-scoped top report authors (resolved names only) grounding the AI
   *  briefing's "who is making reports" line. */
  officerActivity: OfficerActivity[];
  /** Raw fetched inputs, WIDENED to the window's first local midnight so day
   *  groupings can show complete days — NOT scoped to the login window. Any
   *  since-login consumer must re-filter (see filterActivitySince). Identity-
   *  stable once loaded, so safe as useMemo dependencies downstream. */
  reports: ReportGenReport[];
  evidence: EvidenceFileDDB[];
  isLoading: boolean;
  /** True while any dashboard query is (re)fetching — including background
   *  refetches and the Refresh button, which `isLoading` (initial load only)
   *  does not cover. Drives the Refresh button's spinner/disabled state. */
  isFetching: boolean;
  /** True only when the REPORTS query failed — the page is unusable without
   *  reports. An evidence-only failure degrades instead (see evidenceError). */
  isError: boolean;
  error: unknown;
  /** True when the evidence fetch failed while reports succeeded: the
   *  dashboard still renders, with evidence surfaces marked unavailable. */
  evidenceError: boolean;
  refetch: () => void;
  /** True once the roster fetch has resolved either way. Gates the AI
   *  briefing: fired before names resolve, it would bake "Unknown officer"
   *  events into a cached summary and drop unresolved authors from
   *  officerActivity, only for the roster landing to re-key the briefing —
   *  a second billable call. isError still counts as settled so a dead
   *  roster API can't block the briefing forever. */
  rosterSettled: boolean;
  /** Number of ACTIVE officers on the station roster (denominator context —
   *  deactivated members would inflate it; names may still resolve through
   *  the inactive-inclusive roster). */
  rosterSize: number;
  /** True when the roster fetch failed — officer names fall back to raw ids
   *  and the roster denominator is unavailable, but the dashboard still works. */
  rosterError: boolean;
}

/**
 * Loads and aggregates everything the Station Admin dashboard renders, scoped
 * to the caller's own station over the "since last login" window. Reports come
 * from GET /reports and evidence from GET /evidence (both org-scoped by the
 * backend authorizer); the roster resolves officer display names.
 */
export function useStationActivity(): UseStationActivityResult {
  const orgId = useOrgId();
  const window = useLastLoginWindow();
  const enabled = !!orgId;

  // Fetch from the local-midnight FLOOR of the window start so the first day
  // bucket is not clipped at the login time. All views cap at the frozen
  // window end; whole-window metrics, feed, and AI briefing also re-filter the
  // exact start client-side.
  const fetchStart = widenToLocalDayStart(window.windowStart);

  const rosterQuery = useQuery({
    ...stationUsersQuery(orgId ?? ""),
    enabled,
  });

  // Active-only directory for the roster-size denominator (shared warm cache
  // with the app-wide user directory). The inactive-inclusive roster above
  // still resolves names, so a since-deactivated uploader isn't shown as an id.
  const directoryQuery = useQuery({
    ...stationDirectoryQuery(orgId ?? ""),
    enabled,
  });

  const reportsQuery = useQuery({
    queryKey: stationAdminKeys.reports(
      orgId ?? "",
      fetchStart,
      window.windowEnd,
    ),
    queryFn: ({ signal }) =>
      listStationReportsSince(fetchStart, window.windowEnd, signal),
    enabled,
    staleTime: 60_000,
  });

  const evidenceQuery = useQuery({
    queryKey: stationAdminKeys.evidence(
      orgId ?? "",
      fetchStart,
      window.windowEnd,
    ),
    queryFn: ({ signal }) =>
      listStationEvidenceSince(fetchStart, window.windowEnd, signal),
    enabled,
    staleTime: 60_000,
  });

  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    // Invitation-only rows have a null id and cannot be activity actors.
    for (const u of rosterQuery.data?.users ?? []) {
      if (u.id) map.set(u.id, formatOfficer(u));
    }
    return map;
  }, [rosterQuery.data]);

  const metrics = useMemo<StationMetrics>(() => {
    const reports = reportsQuery.data?.reports ?? [];
    const reportsTruncated = reportsQuery.data?.truncated ?? false;
    const evidence = evidenceQuery.data ?? { items: [], truncated: false };
    return aggregateMetrics(reports, reportsTruncated, evidence, {
      start: window.windowStart,
      end: window.windowEnd,
      hasPriorLogin: window.hasPriorLogin,
    });
  }, [reportsQuery.data, evidenceQuery.data, window]);

  const feed = useMemo<ActivityEvent[]>(() => {
    // The fetch is widened to the first day's local midnight; the feed stays
    // scoped to the exact since-last-login window.
    const { reports, evidence } = filterActivitySince(
      reportsQuery.data?.reports ?? [],
      evidenceQuery.data?.items ?? [],
      window.windowStart,
      window.windowEnd,
    );
    return buildActivityFeed(
      reports,
      evidence,
      (id) => (id ? nameById.get(id) : undefined),
    );
  }, [
    reportsQuery.data,
    evidenceQuery.data,
    nameById,
    window.windowStart,
    window.windowEnd,
  ]);

  const officerActivity = useMemo<OfficerActivity[]>(
    () =>
      aggregateOfficerActivity(
        reportsQuery.data?.reports ?? [],
        window.windowStart,
        (id) => (id ? nameById.get(id) : undefined),
        undefined,
        window.windowEnd,
      ),
    [reportsQuery.data, nameById, window.windowStart, window.windowEnd],
  );

  return {
    window,
    metrics,
    feed,
    officerActivity,
    reports: reportsQuery.data?.reports ?? EMPTY_REPORTS,
    evidence: evidenceQuery.data?.items ?? EMPTY_EVIDENCE,
    // No `enabled &&` guard: while orgId is still resolving (queries disabled)
    // the dashboard must read as loading, not as a real all-zero window. React
    // Query v5 keeps isPending true for disabled queries, which is exactly
    // that semantics.
    isLoading: reportsQuery.isPending || evidenceQuery.isPending,
    isFetching:
      reportsQuery.isFetching ||
      evidenceQuery.isFetching ||
      rosterQuery.isFetching ||
      directoryQuery.isFetching,
    isError: reportsQuery.isError,
    error: reportsQuery.error ?? evidenceQuery.error,
    evidenceError: evidenceQuery.isError,
    refetch: () => {
      void reportsQuery.refetch();
      void evidenceQuery.refetch();
      // Unconditionally: focus-refetch is off app-wide, so on a long-lived
      // dashboard this button is the ONLY path that refreshes officer names
      // and the roster denominator. The button already disables on these
      // queries' isFetching, and a no-change roster refetch is identity-
      // stable (structural sharing), so the AI briefing re-keys only when
      // the roster really changed.
      void rosterQuery.refetch();
      void directoryQuery.refetch();
    },
    rosterSettled: rosterQuery.isSuccess || rosterQuery.isError,
    // Active-only directory is the preferred denominator; if it fails, fall
    // back to the inactive-inclusive roster count (a slightly inflated
    // denominator beats the "/ N on roster" context silently vanishing when
    // only the directory query — which has no error surface — is down).
    rosterSize:
      directoryQuery.data?.users?.length ??
      rosterQuery.data?.users?.length ??
      0,
    rosterError: rosterQuery.isError,
  };
}
